import type { AddressState } from '@paid-ui/enums/address';
import type { Address } from '@paid-ui/schemas/zod/address';

import React, { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import { states } from '@paid-ui/constants/address';
import { CountryCode } from '@paid-ui/enums/address';

import { Button } from '../button';
import { Asterisk } from '../form';
import { Input } from '../input';
import { Select, SelectItem } from '../select';

const addressInputVariants = cva('space-y-1', {
  variants: {
    key: {
      unitNumber: 'col-span-1',
      streetNumber: 'col-span-2',
      streetName: 'col-span-3',
      suburb: 'col-span-2 sm:col-span-1',
      postcode: 'col-span-1',
      state: 'col-span-3 sm:col-span-1',
    },
  },
});

export interface AddressInputProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'value' | 'defaultValue' | 'onBlur' | 'onFocus'
  > {
  name?: string;
  value?: Address | '';
  autoFocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  changed?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onValueChange?: (value: Address) => void;
  onManualChange?: (value: boolean) => void;
}

const keys = ['unitNumber', 'streetNumber', 'streetName', 'suburb', 'postcode'] as const;

const defaultAddress: Address = {
  unitNumber: '',
  streetNumber: '',
  streetName: '',
  suburb: '',
  postcode: '',
  state: '' as AddressState,
  country: CountryCode.AU,
};

const labelMap = {
  unitNumber: 'Unit Number',
  streetNumber: 'Street/Lot Number',
  streetName: 'Street Name',
  suburb: 'Suburb/Town',
  postcode: 'Postcode',
  state: 'State',
};

export const AddressInput = forwardRef<HTMLDivElement, AddressInputProps>(
  (
    {
      name,
      value,
      autoFocus,
      disabled,
      readOnly,
      error,
      changed,
      hidden,
      className,
      onChange,
      onBlur,
      onFocus,
      onValueChange,
      onManualChange,
      ...restProps
    },
    ref,
  ) => {
    if (hidden) {
      return null;
    }

    const handleValueChange = (fieldName: keyof Address, fieldValue: string) => {
      onValueChange?.(
        value
          ? {
              ...defaultAddress,
              ...value,
              [fieldName]: fieldValue,
            }
          : {
              ...defaultAddress,
              [fieldName]: fieldValue,
            },
      );
    };

    const handleGoogleMapsSwitch = () => {
      onManualChange?.(false);
    };

    return (
      <div
        ref={ref}
        className={twMerge('grid grid-cols-3 gap-x-3.5 gap-y-5', className)}
        {...restProps}
      >
        {keys.map((key) => (
          <label key={key} className={twMerge(addressInputVariants({ key }))}>
            <div className="flex items-center gap-x-1 text-sm font-medium">
              <span>{labelMap[key]}</span>
              <Asterisk hidden={key === 'unitNumber'} />
            </div>
            <Input
              name={name ? `${name}.${key}` : key}
              value={value ? value[key] : ''}
              autoFocus={autoFocus && key === 'streetNumber'}
              onChange={(event) =>
                handleValueChange(key as keyof Address, event.currentTarget.value)
              }
              onBlur={onBlur}
              onFocus={onFocus}
              disabled={disabled}
              readOnly={readOnly}
              error={error}
              changed={changed}
              compact
            />
          </label>
        ))}
        <label className={twMerge(addressInputVariants({ key: 'state' }))}>
          <div className="flex items-center gap-x-1 text-sm font-medium">
            <span>{labelMap.state}</span>
            <Asterisk />
          </div>
          <Select
            name={name ? `${name}.state` : 'state'}
            value={value ? value.state : ''}
            placeholder="Please select..."
            onValueChange={(value) => handleValueChange('state' as keyof Address, value)}
            onBlur={onBlur as React.FocusEventHandler<HTMLButtonElement>}
            onFocus={onFocus as React.FocusEventHandler<HTMLButtonElement>}
            disabled={disabled}
            readOnly={readOnly}
            error={error}
            changed={changed}
            compact
          >
            {Object.entries(states).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </Select>
        </label>
        {onManualChange ? (
          <p className="col-span-3">
            <Button variant="link" size="sm" onClick={handleGoogleMapsSwitch} full={false}>
              Switch to google maps
            </Button>
          </p>
        ) : null}
      </div>
    );
  },
);

AddressInput.displayName = 'AddressInput';
